/** The fewest outfits a loop holds (owner decision 2); the largest is the whole gallery. */
export const ROTATION_MIN_OUTFITS = 2;
/** The interval's bounds (owner decision 5): 1 to 720 hours, 1 to 30 days. */
export const ROTATION_HOURS_MAX = 720;
export const ROTATION_DAYS_MAX = 30;
/** A days loop's change time unless the person picks another: 09:00 on their clock (owner, 2026-10-19 21:20 PT). */
export const ROTATION_DEFAULT_CHANGE_TIME = '09:00';
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const CHANGE_TIME = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
/** A fresh draft: every day at 09:00, in the order picked (the mockup's defaults); Edit... starts from the loop as it is. */
export function rotationDraft(loop) {
    if (!loop)
        return { every: '1', unit: 'days', changeTime: ROTATION_DEFAULT_CHANGE_TIME, order: 'picked' };
    return { every: String(loop.every), unit: loop.unit, changeTime: loop.changeTime ?? ROTATION_DEFAULT_CHANGE_TIME, order: loop.order };
}
/**
 * Why a draft cannot start, in one plain line, or '' when it can: at least ROTATION_MIN_OUTFITS outfits picked, a whole
 * number of hours (1 to 720) or days (1 to 30), and for a days loop a time of day.
 */
export function rotationDraftRefusal(draft, picked) {
    if (picked < ROTATION_MIN_OUTFITS)
        return `Pick at least ${ROTATION_MIN_OUTFITS} outfits to rotate.`;
    const every = draft.every.trim() === '' ? NaN : Number(draft.every);
    if (!Number.isInteger(every) || every < 1)
        return draft.unit === 'hours' ? 'Enter a whole number of hours, from 1 to 720.' : 'Enter a whole number of days, from 1 to 30.';
    if (draft.unit === 'hours' && every > ROTATION_HOURS_MAX)
        return `At most ${ROTATION_HOURS_MAX} hours (30 days). For longer, choose days.`;
    if (draft.unit === 'days' && every > ROTATION_DAYS_MAX)
        return `At most ${ROTATION_DAYS_MAX} days.`;
    if (draft.unit === 'days' && !CHANGE_TIME.test(draft.changeTime))
        return 'Choose the time of day the outfit changes.';
    return '';
}
/** The definition Start sends for a draft that passed rotationDraftRefusal, with the outfits in the order picked. */
export function rotationDefinition(draft, outfitIds) {
    const every = Number(draft.every);
    return { outfitIds: [...outfitIds], every, unit: draft.unit, ...(draft.unit === 'days' ? { changeTime: draft.changeTime } : {}), order: draft.order };
}
/** "every hour", "every 3 hours", "every day", "every 2 days". */
export function rotationEveryText(every, unit) {
    const word = unit === 'hours' ? 'hour' : 'day';
    return every === 1 ? `every ${word}` : `every ${every} ${word}s`;
}
/**
 * A change's moment in words, on this PC's clock: "today 15:14", "tomorrow 09:00", "Thu 09:00" within the week, else
 * "Nov 3, 09:00" ("Jan 3, 2027, 09:00" in another year). 24-hour clock in every locale (the panel's time field is one).
 */
export function rotationMomentText(ms, options = {}) {
    const { locale, timeZone, now = Date.now() } = options;
    const dayKey = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone });
    const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone }).format(ms);
    const today = dayKey.format(now);
    const day = dayKey.format(ms);
    if (day === today)
        return `today ${time}`;
    if (day === dayKey.format(now + DAY))
        return `tomorrow ${time}`;
    if (ms > now && ms - now < 6 * DAY)
        return `${new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone }).format(ms)} ${time}`;
    const otherYear = day.slice(0, 4) !== today.slice(0, 4);
    return `${new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', ...(otherYear ? { year: 'numeric' } : {}), timeZone }).format(ms)}, ${time}`;
}
/**
 * When a draft started now would change first, on this PC's clock: an hours loop counts from now; a days loop changes at
 * its time of day, the first time after now (today if that time is still to come, else tomorrow). A preview only: gyro-svc
 * computes the real moment in the zone the request sends, which is this PC's.
 */
export function rotationFirstChange(draft, now = Date.now()) {
    const every = Number(draft.every);
    if (!Number.isInteger(every) || every < 1)
        return null;
    if (draft.unit === 'hours')
        return now + every * HOUR;
    if (!CHANGE_TIME.test(draft.changeTime))
        return null;
    const [hours, minutes] = draft.changeTime.split(':').map(Number);
    const at = new Date(now);
    at.setHours(hours, minutes, 0, 0);
    if (at.getTime() <= now)
        at.setDate(at.getDate() + 1);
    return at.getTime();
}
/** "In the order you picked: Witch, Pirate, Astronaut, Chef." or "Random, no repeats until all worn." */
export function rotationOrderText(order, names) {
    return order === 'picked' ? `In the order you picked: ${names.join(', ')}.` : 'Random, no repeats until all worn.';
}
/** "4 outfits" / "1 outfit". */
export function outfitsCountText(count) {
    return `${count} outfit${count === 1 ? '' : 's'}`;
}
export function rotationBannerState(loop) {
    if (loop.available.length === 0)
        return 'ended';
    return loop.paused ? 'paused' : 'running';
}
/**
 * The banner's words from the view: what shows now (`showingName`, the view's `showing`: the loop's outfit, the hand
 * pick while paused, or today's day outfit), when the next change is, which outfits are in the loop, and how many of
 * them the person can have today.
 */
export function rotationBannerWords(loop, names, showingName, options = {}) {
    const state = rotationBannerState(loop);
    const nameOf = (id) => (id === null ? '' : names.get(id) ?? 'an outfit');
    const picked = loop.outfitIds.map((id) => nameOf(id));
    const count = outfitsCountText(loop.outfitIds.length);
    const every = rotationEveryText(loop.every, loop.unit);
    const next = rotationMomentText(loop.nextChangeMs, options);
    const dropped = loop.available.length < loop.outfitIds.length ? ` ${loop.available.length} of ${loop.outfitIds.length} available to you right now.` : '';
    const atTime = loop.unit === 'days' && loop.changeTime ? ` at ${loop.changeTime}` : '';
    if (state === 'ended') {
        return {
            state,
            headline: [{ label: 'Rotation ended:' }, { label: 'none of its', value: count }, { label: 'is available to you right now.' }],
            detail: `${rotationOrderText(loop.order, picked)} The outfits come back by themselves when they are yours again; until then Gyro shows what he would with no outfit picked.`,
        };
    }
    if (state === 'paused') {
        return {
            state,
            headline: [{ label: 'Paused on', value: showingName }],
            detail: `Resume to keep rotating ${count} ${every}${atTime}; the loop is at ${nameOf(loop.nowId) || 'none'}, next change ${next}.${dropped}`,
        };
    }
    const nowLoop = loop.nowId === null ? '' : nameOf(loop.nowId);
    const overridden = nowLoop !== '' && nowLoop !== showingName ? ` Today ${showingName} shows for everyone; the loop continues after.` : '';
    return {
        state,
        headline: [{ label: 'Rotating', value: count }, { label: `${every}${atTime}` }, { label: 'now:', value: nowLoop || showingName }, { label: 'next change', value: next }],
        detail: `${rotationOrderText(loop.order, picked)} Picking any outfit by hand pauses the loop.${dropped}${overridden}`,
    };
}
/** The tray's line: "4 picked · Witch, Pirate, Astronaut, Chef" (names in picked order; more than 8 are counted). */
export function rotationTrayText(names) {
    const shown = names.slice(0, 8);
    const rest = names.length - shown.length;
    return { count: `${names.length} picked`, list: rest > 0 ? `${shown.join(', ')} and ${rest} more` : shown.join(', ') };
}
/** The panel's "Starts now with Witch · next change tomorrow 09:00" line, or the refusal when the draft cannot start. */
export function rotationStartLine(draft, firstName, options = {}) {
    const first = rotationFirstChange(draft, options.now ?? Date.now());
    if (first === null || !firstName)
        return '';
    return `Starts now with ${firstName} · next change ${rotationMomentText(first, options)}`;
}
//# sourceMappingURL=mascotRotationModel.js.map